'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MembershipCard from './components/MembershipCard';
import { createClient } from '@/lib/supabase/client';

interface MemberData {
  name: string;
  id: string;
  status: 'Active' | 'Inactive';
  bio: string;
  phone?: string;
  profilePictureUrl?: string;
}

export default function VerifyPage() {
  const [inputId, setInputId] = useState('');
  const [member, setMember] = useState<MemberData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  const handleVerify = async () => {
    if (!inputId.trim()) {
      setError('Please enter a Membership ID.');
      return;
    }
    setLoading(true);
    setError('');
    setMember(null);
    setVerified(false);

    try {
      const supabase = createClient();
      const { data, error: dbError } = await supabase
        .from('waitlist')
        .select('first_name, last_name, membership_id, bio, phone_number, profile_picture_url')
        .ilike('membership_id', inputId.trim())
        .maybeSingle();

      if (dbError) throw dbError;

      if (data) {
        setMember({
          name: `${data.first_name} ${data.last_name}`.trim(),
          id: data.membership_id,
          status: 'Active',
          bio: data.bio || 'Conect Club member.',
          phone: data.phone_number || undefined,
          profilePictureUrl: data.profile_picture_url || undefined,
        });
        setVerified(true);
      } else {
        setError('Invalid Membership ID. Please try again.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setInputId('');
    setMember(null);
    setError('');
    setVerified(false);
  };

  return (
    <>
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main className="min-h-screen bg-void pt-24 pb-20">
        {/* Radial glow */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 30%, rgba(123,47,247,0.12) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 max-w-2xl mx-auto px-6">
          {/* Page header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(123,47,247,0.4)] bg-[rgba(123,47,247,0.08)] mb-6">
              <span className="w-2 h-2 rounded-full bg-violet-DEFAULT" style={{ background: '#7B2FF7' }} />
              <span className="text-[11px] font-medium tracking-[0.15em] uppercase" style={{ color: '#7B2FF7' }}>
                Member Verification
              </span>
            </div>
            <h1 className="font-serif font-bold mb-4" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: 'var(--cream)' }}>
              Verify Your{' '}
              <em className="not-italic" style={{ color: '#7B2FF7' }}>Membership</em>
            </h1>
            <p className="text-base font-light leading-relaxed" style={{ color: 'rgba(240,230,211,0.5)' }}>
              Enter your Membership ID to access your digital membership card.
            </p>
          </div>

          {/* Verification form */}
          {!verified && (
            <div
              className="rounded-2xl p-8 mb-8"
              style={{ background: '#111124', border: '1px solid rgba(240,230,211,0.08)' }}
            >
              <label className="block text-[11px] font-medium tracking-[0.15em] uppercase mb-3" style={{ color: 'rgba(240,230,211,0.5)' }}>
                Membership ID
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={inputId}
                  onChange={(e) => { setInputId(e.target.value); setError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                  placeholder="Enter Membership ID (e.g. CC-2026-0001)"
                  className="flex-1 rounded-xl px-5 py-3.5 text-sm font-medium outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(240,230,211,0.05)',
                    border: error ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(240,230,211,0.12)',
                    color: 'var(--cream)',
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                />
                <button
                  onClick={handleVerify}
                  disabled={loading}
                  className="cta-pill px-7 py-3.5 text-sm font-semibold whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Verifying...
                    </span>
                  ) : 'Verify Membership'}
                </button>
              </div>

              {/* Error message */}
              {error && (
                <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: '#ef4444', flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  <p className="text-sm font-medium" style={{ color: '#ef4444' }}>{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Membership Card */}
          {verified && member && (
            <div>
              <MembershipCard member={member} />
              <div className="mt-6 text-center">
                <button
                  onClick={handleReset}
                  className="text-sm font-medium underline-expand"
                  style={{ color: 'rgba(240,230,211,0.5)' }}
                >
                  Verify another member
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
